import { useId, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useWatch } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label, RequiredMark } from '@/components/ui/label';
import {
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useDiscardGuard } from '@/shared/hooks/useDiscardGuard';
import {
  ConnectionSelect,
  CreateConnectionSheet,
  useConnections,
} from '@/features/connections';

import { isExecutionImageError } from '../errors';
import { useCreateExecutionImage } from '../hooks/useCreateExecutionImage';
import { useExecutionImages } from '../hooks/useExecutionImages';
import {
  ADD_EXECUTION_IMAGE_DEFAULT_VALUES,
  addExecutionImageFormSchema,
} from '../types';
import type { AddExecutionImageFormValues } from '../types';

/**
 * Add an image to the catalog (US3): a registry connection, an image
 * reference, and a required label.
 *
 * The catalog-wide `image_reference` uniqueness check (research.md D3,
 * FR-008a) runs here, client-side, against the already-loaded
 * `useExecutionImages()` list — the backend applies no such constraint.
 */
export function AddExecutionImageSheet({
  workspaceId,
  open,
  onClose,
}: {
  workspaceId: string;
  open: boolean;
  onClose: () => void;
}) {
  const { images } = useExecutionImages(workspaceId);
  const create = useCreateExecutionImage(workspaceId);

  const {
    control,
    register,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    formState: { errors, isDirty },
  } = useForm<AddExecutionImageFormValues>({
    resolver: zodResolver(addExecutionImageFormSchema),
    defaultValues: ADD_EXECUTION_IMAGE_DEFAULT_VALUES,
  });

  const connectionId = useWatch({ control, name: 'connectionId' });

  const connectionIdId = useId();
  const imageReferenceId = useId();
  const labelId = useId();

  const serverError = isExecutionImageError(create.error)
    ? create.error
    : undefined;
  const genericError =
    create.error && !serverError
      ? 'The request failed. Please try again.'
      : undefined;
  const formMessage =
    serverError?.field === 'form' ? serverError.message : genericError;
  const connectionIdMessage =
    serverError?.field === 'connectionId' ? serverError.message : undefined;

  const submit = handleSubmit((values) => {
    const reference = values.imageReference.trim();
    const alreadyInCatalog = (images ?? []).some(
      (image) => image.image_reference === reference,
    );
    if (alreadyInCatalog) {
      setError('imageReference', {
        message: 'This image reference is already in the catalog.',
      });
      return;
    }

    create.mutate(
      { ...values, imageReference: reference },
      {
        onSuccess: () => {
          onClose();
        },
      },
    );
  });

  // A successful save calls `onClose` directly, so it never asks.
  const { requestClose, dialog } = useDiscardGuard(isDirty, onClose);

  const [creatingConnection, setCreatingConnection] = useState(false);
  // The same read ConnectionSelect makes (one cache key, no second request).
  const registries = useConnections(workspaceId);
  const noRegistry =
    !registries.isPending &&
    !registries.loadFailed &&
    !(registries.connections ?? []).some(
      (connection) => connection.enabled && connection.type === 'oci_registry',
    );
  const [connectionStatus, setConnectionStatus] = useState<string | null>(null);
  const connectionFieldRef = useRef<HTMLButtonElement>(null);

  return (
    <>
      <Sheet
        open={open}
        onOpenChange={(next) => {
          if (create.isPending) return;
          if (!next) requestClose();
        }}
      >
        <SheetContent className="sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>Add an execution image</SheetTitle>
            <SheetDescription>
              Adds an image from an enabled registry connection to this
              workspace&apos;s catalog.
            </SheetDescription>
          </SheetHeader>

          <form
            onSubmit={(event) => {
              void submit(event);
            }}
            className="flex min-h-0 flex-1 flex-col"
          >
            <SheetBody className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-0.5">
                  <span id={connectionIdId} className="text-sm font-medium">
                    Connection
                  </span>
                  <RequiredMark />
                </div>
                <ConnectionSelect
                  workspaceId={workspaceId}
                  providerType="oci_registry"
                  value={connectionId}
                  onChange={(id) => {
                    setValue('connectionId', id, { shouldDirty: true });
                    if (serverError?.field === 'connectionId') clearErrors();
                  }}
                  disabled={create.isPending}
                  aria-label="Connection"
                  ref={connectionFieldRef}
                  onCreateRequest={() => {
                    setCreatingConnection(true);
                  }}
                />
                {noRegistry ? (
                  // Registries live under Integrations now (spec 229); this
                  // sheet says where, and still offers "Create one" here.
                  <p className="text-muted-foreground text-xs">
                    No registry connected yet. Connect one under{' '}
                    <Link
                      to="/workspaces/$workspaceId/integrations/connections"
                      params={{ workspaceId }}
                      className="text-foreground focus-visible:ring-ring rounded underline underline-offset-2 focus-visible:ring-3 focus-visible:outline-none"
                    >
                      Integrations → Connections
                    </Link>
                    .
                  </p>
                ) : null}
                {connectionStatus ? (
                  <p
                    role="status"
                    aria-live="polite"
                    className="text-muted-foreground text-xs"
                  >
                    {connectionStatus}
                  </p>
                ) : null}
                {errors.connectionId ? (
                  <p className="text-destructive text-xs">
                    {errors.connectionId.message}
                  </p>
                ) : null}
              </div>

              <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-0.5">
                  <Label htmlFor={imageReferenceId}>Image reference</Label>
                  <RequiredMark />
                </div>
                <Input
                  id={imageReferenceId}
                  {...register('imageReference')}
                  disabled={create.isPending}
                  autoFocus
                  placeholder="registry.example.com/team/image:tag"
                  className="font-mono"
                  aria-invalid={errors.imageReference ? true : undefined}
                />
                {errors.imageReference ? (
                  <p className="text-destructive text-xs">
                    {errors.imageReference.message}
                  </p>
                ) : null}
              </div>

              <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-0.5">
                  <Label htmlFor={labelId}>Label</Label>
                  <RequiredMark />
                </div>
                <Input
                  id={labelId}
                  {...register('label')}
                  disabled={create.isPending}
                  aria-invalid={errors.label ? true : undefined}
                />
                {errors.label ? (
                  <p className="text-destructive text-xs">
                    {errors.label.message}
                  </p>
                ) : null}
              </div>

              {connectionIdMessage ? (
                <p role="alert" className="text-destructive text-sm">
                  {connectionIdMessage}
                </p>
              ) : null}
              {formMessage ? (
                <p role="alert" className="text-destructive text-sm">
                  {formMessage}
                </p>
              ) : null}
            </SheetBody>

            <SheetFooter>
              <SheetClose
                render={<Button type="button" variant="outline" />}
                disabled={create.isPending}
              >
                Cancel
              </SheetClose>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? 'Adding…' : 'Add'}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>
      {/* The registry connection this form needs, made without leaving it
          (spec 124 US1). Beside the sheet, so Esc closes it alone. */}
      <CreateConnectionSheet
        workspaceId={workspaceId}
        open={creatingConnection}
        providerType="oci_registry"
        finalFocus={connectionFieldRef}
        onCreated={(connection) => {
          if (connection.enabled) {
            setValue('connectionId', connection.id, { shouldDirty: true });
            setConnectionStatus(`Connection ${connection.name} added`);
          } else {
            setConnectionStatus(
              `Connection ${connection.name} was saved disabled — enable it on the Connections page to use it here`,
            );
          }
        }}
        onClose={() => {
          setCreatingConnection(false);
        }}
      />
      {dialog}
    </>
  );
}
